import { Link } from 'react-router-dom'
import { useSiteSettings } from '../context/SiteSettingsContext'
import { useLanguage } from '../context/LanguageContext'
import { useEffect, useState } from 'react'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../firebase'
import { sectionPath } from '../utils/routePaths'

export default function Footer() {
  const settings = useSiteSettings()
  const { language, t } = useLanguage()
  const [categories, setCategories] = useState([])

  useEffect(() => {
    getDocs(collection(db, 'categories'))
      .then(snap => setCategories(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {})
  }, [])

  const navLinks = [
    { label: t('nav_explore'), to: `/${language}` },
    { label: t('nav_categories'), to: sectionPath(language, 'categories') },
    { label: t('nav_about'), to: sectionPath(language, 'about') },
  ]

  const socialLinks = [
    settings.facebookUrl && { href: settings.facebookUrl, label: "Facebook", icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
    )},
    settings.instagramUrl && { href: settings.instagramUrl, label: "Instagram", icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
        <circle cx="12" cy="12" r="4"/>
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
      </svg>
    )},
    settings.tiktokUrl && { href: settings.tiktokUrl, label: "TikTok", icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.17 8.17 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/></svg>
    )},
  ].filter(Boolean)

  return (
    <footer style={{ background: '#0a0c12', marginTop: 'auto' }}>
      <div style={{ width: '100%', padding: '72px 64px 48px', display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr', gap: 48 }} className="footer-grid">

        {/* Brand col */}
        <div>
          <Link to={`/${language}`} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', marginBottom: 16 }}>
            <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
              <path d="M2 22 L10 8 L14 14 L18 10 L26 22 Z" stroke="white" strokeWidth="1.8" strokeLinejoin="round" fill="none"/>
              <path d="M18 10 L22 6 L26 10" stroke="white" strokeWidth="1.8" strokeLinejoin="round" fill="none"/>
            </svg>
            <span style={{ fontFamily: 'Geist, sans-serif', fontSize: 20, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
              {settings.siteName}
            </span>
          </Link>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, lineHeight: 1.7, marginBottom: 28, maxWidth: 240, fontFamily: 'Geist, sans-serif' }}>
            {language === 'ka' && settings.heroSubtitleKa ? settings.heroSubtitleKa : settings.heroSubtitle}
          </p>

          {/* Social icons */}
          {socialLinks.length > 0 && (
            <div style={{ display: 'flex', gap: 10 }}>
              {socialLinks.map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  style={{ width: 38, height: 38, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'all 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.background = 'transparent'; }}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div>
          <p style={{ fontFamily: 'Geist, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 20 }}>
            {t('footer_navigation')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {navLinks.map(link => (
              <Link key={link.to} to={link.to} style={{ color: 'rgba(255,255,255,0.55)', textDecoration: 'none', fontSize: 14, fontFamily: 'Geist, sans-serif', fontWeight: 500, transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = '#fff'}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.55)'}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Categories */}
        <div>
          <p style={{ fontFamily: 'Geist, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 20 }}>
            {t('footer_categories')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {categories.slice(0, 5).map(cat => (
              <Link key={cat.id} to={`${sectionPath(language, 'categories')}?filter=${encodeURIComponent(cat.name)}`}
                style={{ color: 'rgba(255,255,255,0.55)', textDecoration: 'none', fontSize: 14, fontFamily: 'Geist, sans-serif', fontWeight: 500, transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = '#fff'}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.55)'}
              >
                {language === 'ka' && cat.nameGeo ? cat.nameGeo : cat.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div>
          <p style={{ fontFamily: 'Geist, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 20 }}>
            {t('footer_contact')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {settings.contactEmail && (
              <a href={`mailto:${settings.contactEmail}`} style={{ color: 'rgba(255,255,255,0.55)', textDecoration: 'none', fontSize: 14, fontFamily: 'Geist, sans-serif', fontWeight: 500, transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = '#fff'}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.55)'}
              >
                {settings.contactEmail}
              </a>
            )}
            <Link to={sectionPath(language, 'about')} style={{ color: 'rgba(255,255,255,0.55)', textDecoration: 'none', fontSize: 14, fontFamily: 'Geist, sans-serif', fontWeight: 500, transition: 'color 0.2s' }}
              onMouseEnter={e => e.target.style.color = '#fff'}
              onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.55)'}
            >
              {t('nav_about')}
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{ width: '100%', padding: '20px 64px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }} className="footer-bottom">
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontFamily: 'Geist, sans-serif', margin: 0 }}>
          © {new Date().getFullYear()} {settings.siteName}. {settings.footerCopyright}
        </p>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontFamily: 'Geist, sans-serif', margin: 0 }}>
          {t('footer_all_rights')}
        </p>
      </div>

      <style>{`
        @media (max-width: 1024px) { .footer-grid { grid-template-columns: 1fr 1fr !important; } }
        @media (max-width: 768px) {
          .footer-grid { grid-template-columns: 1fr !important; padding: 48px 20px 32px !important; gap: 36px !important; }
          .footer-bottom { padding: 20px !important; flex-direction: column !important; align-items: flex-start !important; }
        }
      `}</style>
    </footer>
  )
}
